// Server capability detection
export const detectServerCapabilities = async (videoSrc) => {
  try {
    // Test with a small range request
    const response = await fetch(videoSrc, {
      method: 'HEAD',
      cache: 'no-cache'
    });

    const acceptsRanges = response.headers.get('accept-ranges') === 'bytes';
    const contentLength = parseInt(response.headers.get('content-length') || '0');
    const supportsPartialContent = response.status === 200 || response.status === 206;

    // Additional test with actual range request
    let rangeSupported = false;
    if (acceptsRanges && contentLength > 0) {
      try {
        const rangeResponse = await fetch(videoSrc, {
          headers: { 'Range': 'bytes=0-1023' }, // Test 1KB range
          cache: 'no-cache'
        });
        rangeSupported = rangeResponse.status === 206;
      } catch (error) {
        rangeSupported = false;
      }
    }

    return {
      supportsRanges: rangeSupported,
      totalSize: contentLength,
      acceptsRanges,
      supportsPartialContent,
      canStream: rangeSupported && contentLength > 1024 * 1024 // Only stream files > 1MB
    };
  } catch (error) {
    console.warn('Server capability detection failed:', error);
    return {
      supportsRanges: false,
      totalSize: 0,
      acceptsRanges: false,
      supportsPartialContent: false,
      canStream: false
    };
  }
};

// Video streaming utilities for chunked loading
export class VideoChunkLoader {
  constructor(videoSrc, chunkSize = 1024 * 1024) { // 1MB chunks by default
    this.videoSrc = videoSrc;
    this.chunkSize = chunkSize;
    this.chunks = [];
    this.totalSize = 0;
    this.loadedSize = 0;
    this.isLoading = false;
    this.abortController = null;
    this.serverCapabilities = null;
  }

  async getVideoInfo() {
    try {
      const response = await fetch(this.videoSrc, {
        method: 'HEAD',
        cache: 'no-cache'
      });

      this.totalSize = parseInt(response.headers.get('content-length') || '0');
      const acceptsRanges = response.headers.get('accept-ranges') === 'bytes';

      return {
        totalSize: this.totalSize,
        supportsRanges: acceptsRanges,
        contentType: response.headers.get('content-type')
      };
    } catch (error) {
      console.error('Failed to get video info:', error);
      return { totalSize: 0, supportsRanges: false };
    }
  }

  async loadChunk(start, end, onProgress = null) {
    try {
      this.abortController = new AbortController();

      const response = await fetch(this.videoSrc, {
        headers: {
          'Range': `bytes=${start}-${end}`
        },
        signal: this.abortController.signal,
        cache: 'no-cache'
      });

      if (!response.ok) {
        throw new Error(`Failed to load chunk: ${response.status}`);
      }

      const reader = response.body.getReader();
      const chunks = [];
      let receivedLength = 0;
      const expectedLength = end - start + 1;

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        chunks.push(value);
        receivedLength += value.length;

        if (onProgress) {
          const chunkProgress = (receivedLength / expectedLength) * 100;
          onProgress(chunkProgress, receivedLength, expectedLength);
        }
      }

      // Combine chunks into single Uint8Array
      const allChunks = new Uint8Array(receivedLength);
      let position = 0;
      for (const chunk of chunks) {
        allChunks.set(chunk, position);
        position += chunk.length;
      }

      return {
        data: allChunks,
        start,
        end,
        size: receivedLength
      };
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Chunk loading aborted');
      } else {
        console.error('Chunk loading error:', error);
      }
      throw error;
    }
  }

  async loadInitialChunks(numChunks = 3, onProgress = null) {
    const info = await this.getVideoInfo();

    if (!info.supportsRanges || info.totalSize === 0) {
      // Fallback to regular loading
      return this.loadRegular(onProgress);
    }

    const chunkPromises = [];

    for (let i = 0; i < numChunks; i++) {
      const start = i * this.chunkSize;
      const end = Math.min(start + this.chunkSize - 1, info.totalSize - 1);

      chunkPromises.push(
        this.loadChunk(start, end, (chunkProgress, loaded, total) => {
          if (onProgress) {
            const overallProgress = ((i * this.chunkSize + loaded) / info.totalSize) * 100;
            onProgress(Math.min(overallProgress, 100), i + 1, numChunks);
          }
        })
      );
    }

    try {
      const chunks = await Promise.all(chunkPromises);
      this.chunks = chunks;
      this.loadedSize = chunks.reduce((sum, chunk) => sum + chunk.size, 0);

      return {
        chunks,
        totalLoaded: this.loadedSize,
        totalSize: info.totalSize,
        progress: (this.loadedSize / info.totalSize) * 100
      };
    } catch (error) {
      console.error('Failed to load initial chunks:', error);
      throw error;
    }
  }

  async loadRegular(onProgress = null) {
    // Fallback for servers that don't support range requests
    try {
      const response = await fetch(this.videoSrc, {
        cache: 'no-cache'
      });

      if (!response.ok) {
        throw new Error(`Failed to load video: ${response.status}`);
      }

      const reader = response.body.getReader();
      const contentLength = parseInt(response.headers.get('content-length') || '0');
      let receivedLength = 0;
      const chunks = [];

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        chunks.push(value);
        receivedLength += value.length;

        if (onProgress && contentLength > 0) {
          const progress = (receivedLength / contentLength) * 100;
          onProgress(progress, receivedLength, contentLength);
        }
      }

      return {
        chunks,
        totalLoaded: receivedLength,
        totalSize: contentLength,
        progress: 100
      };
    } catch (error) {
      console.error('Regular video loading failed:', error);
      throw error;
    }
  }

  createBlobURL(chunks) {
    const allChunks = chunks || this.chunks;
    if (!allChunks || allChunks.length === 0) return null;

    try {
      // Combine all chunk data
      const totalSize = allChunks.reduce((sum, chunk) => sum + chunk.data.length, 0);
      const combinedData = new Uint8Array(totalSize);
      let position = 0;

      for (const chunk of allChunks) {
        combinedData.set(chunk.data, position);
        position += chunk.data.length;
      }

      const blob = new Blob([combinedData], { type: 'video/mp4' });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Failed to create blob URL:', error);
      return null;
    }
  }

  abort() {
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  cleanup() {
    this.abort();
    this.chunks = [];
    this.loadedSize = 0;
  }
}

// Adaptive streaming based on connection
export const getOptimalChunkSize = () => {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  if (!connection) {
    return 1024 * 1024; // 1MB default
  }

  const effectiveType = connection.effectiveType;

  switch (effectiveType) {
    case 'slow-2g':
      return 256 * 1024; // 256KB
    case '2g':
      return 512 * 1024; // 512KB
    case '3g':
      return 1024 * 1024; // 1MB
    case '4g':
      return 2 * 1024 * 1024; // 2MB
    default:
      return 1024 * 1024; // 1MB
  }
};